import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useApp } from "./AppContext";
import { useI18n } from "../i18n";
import { addToCart, cartCount, cartStorageKey, removeFromCart, sanitizeCart, setCartQty } from "../config/cart";

// ตะกร้าแยกต่อร้าน เก็บใน localStorage `lucid:cart:<orgId>` (เก็บแค่ข้อมูลพอคำนวณราคาใหม่ ไม่เก็บราคา)
const CartContext = createContext(null);

const load = (orgId) => {
  if (!orgId) return [];
  try {
    return sanitizeCart(JSON.parse(localStorage.getItem(cartStorageKey(orgId)) || "[]"));
  } catch (e) {
    return [];
  }
};

const save = (orgId, items) => {
  if (!orgId) return;
  try {
    localStorage.setItem(cartStorageKey(orgId), JSON.stringify(items));
  } catch (e) {
    // เต็ม/ถูกปิด — ใช้ตะกร้าในหน่วยความจำต่อไป
  }
};

export function CartProvider({ children }) {
  const { orgId } = useApp();
  const [state, setState] = useState({ orgId: "", items: [] });

  // เปลี่ยนร้าน = โหลดตะกร้าของร้านนั้น
  useEffect(() => {
    setState({ orgId, items: load(orgId) });
  }, [orgId]);

  // ตะกร้าแท็บอื่นเปลี่ยน -> ตามให้ทัน
  useEffect(() => {
    const onStorage = (event) => {
      if (orgId && event.key === cartStorageKey(orgId)) setState({ orgId, items: load(orgId) });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [orgId]);

  const items = useMemo(() => (state.orgId === orgId ? state.items : []), [state, orgId]);

  const commit = useCallback(
    (update) => {
      setState((prev) => {
        const base = prev.orgId === orgId ? prev.items : load(orgId);
        const next = update(base);
        save(orgId, next);
        return { orgId, items: next };
      });
    },
    [orgId]
  );

  /** คืนสถานะ added | replaced | merged | duplicate (อ่านจากตะกร้าปัจจุบัน ไม่ใช่ใน setState เพื่อให้ได้ค่าทันที) */
  const add = useCallback(
    (item) => {
      const result = addToCart(items, item);
      if (result.status !== "duplicate") commit(() => result.items);
      return result.status;
    },
    [items, commit]
  );

  const value = useMemo(
    () => ({
      items,
      count: cartCount(items),
      add,
      remove: (id) => commit((rows) => removeFromCart(rows, id)),
      setQty: (id, qty) => commit((rows) => setCartQty(rows, id, qty)),
      clear: () => commit(() => []),
    }),
    [items, add, commit]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);

/** ใส่ตะกร้า + แจ้งผลด้วย toast (ใช้ร่วมทุกปุ่ม "ใส่ตะกร้า") */
export const useAddToCart = () => {
  const { add } = useCart();
  const { toast } = useApp();
  const { tx } = useI18n();
  return useCallback(
    (item) => {
      const status = add(item);
      const messages = {
        added: [tx("ใส่ตะกร้าแล้ว", "Added to cart"), "success"],
        merged: [tx("เพิ่มจำนวนในตะกร้าแล้ว", "Quantity updated in cart"), "success"],
        replaced: [tx("เปลี่ยนแพ็กเกจหลักในตะกร้าแล้ว (มีได้ 1 แพ็กเกจ)", "Main plan in cart replaced (only one allowed)"), "success"],
        duplicate: [tx("รายการนี้อยู่ในตะกร้าแล้ว", "Already in your cart"), "error"],
      };
      const [message, tone] = messages[status];
      toast(message, tone);
      return status;
    },
    [add, toast, tx]
  );
};
